import React, { useState, useEffect, useMemo } from "react"
import { Row, Col } from "reactstrap"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import api from "API/Api"
import jsPDF from "jspdf"
import "jspdf-autotable"
import loader from "./../../assets/loading/loader.gif"

const ProcurementHistory = () => {
  const [procType, setProcType] = useState("vlcc")
  const [allPlants, setAllPlants] = useState([])
  const [plants, setPlants] = useState([])
  const [allCenters, setAllCenters] = useState([])
  const [centers, setCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [collections, setCollections] = useState([])
  const [producers, setProducers] = useState([])
  const [vendors, setVendors] = useState([])
  const [companies, setCompanies] = useState([])

  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [results, setResults] = useState([])
  const [totals, setTotals] = useState(null)
  const [search, setSearch] = useState("")

  // Display params checkboxes
  const [showFat, setShowFat] = useState(true)
  const [showSnf, setShowSnf] = useState(true)
  const [showQty, setShowQty] = useState(false)
  const [showRate, setShowRate] = useState(false)
  const [showAmount, setShowAmount] = useState(false)

  // Default dates: 7 days ago to today
  const today = new Date().toISOString().split("T")[0]
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0]

  useEffect(() => { loadData() }, [])

  const loadData = async () => {
    try {
      const [cResp, pResp, coResp] = await Promise.all([
        api.get("centers"), api.get("plants"), api.get("companies")
      ])
      const ccData = Array.isArray(cResp.data.data) ? cResp.data.data : cResp.data.data?.data || []
      setAllCenters(ccData); setCenters(ccData)
      const pData = Array.isArray(pResp.data.data) ? pResp.data.data : pResp.data.data?.data || []
      setAllPlants(pData); setPlants(pData)
      setCompanies(Array.isArray(coResp.data.data) ? coResp.data.data : coResp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }

  const showCompany = companies.length > 1

  const companyChange = async e => {
    const val = e.target.value
    setCenters(allCenters); setRoutes([]); setCollections([]); setProducers([]); setVendors([])
    if (val && val !== "") {
      try { const r = await api.get("plants-by-company/" + val); setPlants(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setPlants(allPlants)
  }
  const plantChange = async e => {
    const val = e.target.value; setRoutes([]); setCollections([]); setProducers([]); setVendors([])
    if (val && val !== "") {
      try { const r = await api.get("chillings-by-plant/" + val); setCenters(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    } else setCenters(allCenters)
  }
  const ccChange = async e => {
    setCollections([]); setProducers([]); setVendors([])
    try {
      const u = !e.target.value || e.target.value == "0" ? "routes" : "route-cc/" + e.target.value
      const r = await api.get(u); setRoutes(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch(e){}
  }
  const routeChange = async e => {
    setProducers([])
    try {
      const u = !e.target.value || e.target.value == "0" ? "collection-centers" : "collection-center-route/" + e.target.value
      const r = await api.get(u); setCollections(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch(e){}
    // Load vendors for CC type
    if (procType === "cc") {
      try { const r = await api.get("bulk-vendors"); setVendors(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch(e){}
    }
  }
  const centerChange = async e => {
    try {
      const u = !e.target.value || e.target.value == "0" ? "producers" : "producer-center/" + e.target.value
      const r = await api.get(u); setProducers(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || [])
    } catch(e){}
  }

  const handleSubmit = async (e, v) => {
    setLoading(true)
    try {
      v.procurementType = procType
      if (companies.length === 1) v.company = companies[0].id
      const resp = await api.post("reports/procurement-history", v)
      if (resp.data.success) {
        setResults(resp.data.data || [])
        setTotals(resp.data.totals || null)
        setSubmitted(true)
      }
    } catch (err) { console.error(err) }
    setLoading(false)
  }

  const filtered = useMemo(() => {
    if (!search) return results
    const s = search.toLowerCase()
    return results.filter(r => Object.values(r).some(v => v && String(v).toLowerCase().includes(s)))
  }, [results, search])

  const exportCSV = () => {
    if (!filtered.length) return
    const headers = ["Date", "Entries", "Suppliers"]
    if (showQty) headers.push("Quantity (Ltrs)")
    if (showFat) headers.push("Avg FAT")
    if (showSnf) headers.push("Avg SNF")
    if (showRate) headers.push("Avg Rate")
    if (showAmount) headers.push("Total Amount")
    const rows = filtered.map(r => {
      const row = [r.date, r.entries, r.suppliers]
      if (showQty) row.push(r.totalQty)
      if (showFat) row.push(r.avgFat)
      if (showSnf) row.push(r.avgSnf)
      if (showRate) row.push(r.avgRate)
      if (showAmount) row.push(r.totalAmount)
      return row
    })
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const u = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = u; a.download = "ProcurementHistory.csv"; a.click()
  }

  const downloadPdf = () => {
    const pdf = new jsPDF("l")
    pdf.autoTable({ html: "#procHistoryTbl", styles: { fontSize: 7 } })
    pdf.save("ProcurementHistory.pdf")
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader notToPrint">
        <Row><Col md={12}><h4>Procurement History</h4></Col></Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal notToPrint" onValidSubmit={(e, v) => handleSubmit(e, v)}>
          {/* Row 1: Procurement Type, Plant, CC, Route */}
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Procurement Type <span className="text-danger">*</span></label>
                <div style={{ display: "flex", gap: "20px", paddingTop: "6px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontWeight: procType === "vlcc" ? 600 : 400 }}>
                    <input type="radio" name="procTypeRadio" checked={procType === "vlcc"} onChange={() => { setProcType("vlcc"); setVendors([]) }} /> VLCC
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontWeight: procType === "cc" ? 600 : 400 }}>
                    <input type="radio" name="procTypeRadio" checked={procType === "cc"} onChange={() => { setProcType("cc"); setProducers([]) }} /> CC
                  </label>
                </div>
              </div>
            </Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="plant" label="Plant" onChange={plantChange}><option value="">All Plants</option>{plants.map((p, i) => <option key={i} value={p.id}>{p.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="chillingCenter" label="Chilling Center" onChange={ccChange}><option value="">All Chilling Centers</option>{centers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
            <Col md={3}><div className="mb-3"><AvField type="select" name="route" label="Route" onChange={routeChange}><option value="">All Routes</option>{routes.map((r, i) => <option key={i} value={r.id}>{r.routeName}</option>)}</AvField></div></Col>
          </Row>

          {/* Row 2: Center/Vendor, Producer (VLCC only), Milk Type, Period From */}
          <Row>
            {procType === "vlcc" ? (
              <>
                <Col md={3}><div className="mb-3"><AvField type="select" name="center" label="Center" onChange={centerChange}><option value="">All</option>{collections.map((c, i) => <option key={i} value={c.id}>{c.centerName}</option>)}</AvField></div></Col>
                <Col md={3}><div className="mb-3"><AvField type="select" name="producer" label="Producer"><option value="">All Producers</option>{producers.map((p, i) => <option key={i} value={p.id}>{p.producerName}</option>)}</AvField></div></Col>
              </>
            ) : (
              <Col md={3}><div className="mb-3"><AvField type="select" name="vendor" label="Center/Vendor"><option value="">All</option>{vendors.map((v, i) => <option key={i} value={v.id}>{v.vendorName}</option>)}</AvField></div></Col>
            )}
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Milk Type</label>
                <div style={{ paddingTop: "6px" }}>
                  <AvRadioGroup inline name="milkType" value="both">
                    <AvRadio label="Both" value="both" />
                    <AvRadio label="Cow" value="cow" />
                    <AvRadio label="Buffalo" value="buffalo" />
                  </AvRadioGroup>
                </div>
              </div>
            </Col>
            <Col md={3}><div className="mb-3"><AvField type="date" name="periodFrom" label="Procurement Period From *" value={weekAgo} required /></div></Col>
          </Row>

          {/* Row 3: Period To, Shift, Display Parameters, Submit */}
          <Row>
            <Col md={3}><div className="mb-3"><AvField type="date" name="periodTo" label="To *" value={today} required /></div></Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Shift <span className="text-danger">*</span></label>
                <div style={{ paddingTop: "6px" }}>
                  <AvRadioGroup inline name="shift" value="both" required>
                    <AvRadio label="Both" value="both" />
                    <AvRadio label="AM" value="am" />
                    <AvRadio label="PM" value="pm" />
                  </AvRadioGroup>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label">Display Parameters</label>
                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", paddingTop: "6px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", fontSize: "13px" }}>
                    <input type="checkbox" checked={showFat} onChange={e => setShowFat(e.target.checked)} style={{ accentColor: "#0d6efd" }} /> FAT
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", fontSize: "13px" }}>
                    <input type="checkbox" checked={showSnf} onChange={e => setShowSnf(e.target.checked)} style={{ accentColor: "#0d6efd" }} /> SNF
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", fontSize: "13px" }}>
                    <input type="checkbox" checked={showQty} onChange={e => setShowQty(e.target.checked)} /> Quantity
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", fontSize: "13px" }}>
                    <input type="checkbox" checked={showRate} onChange={e => setShowRate(e.target.checked)} /> Rate
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "4px", cursor: "pointer", fontSize: "13px" }}>
                    <input type="checkbox" checked={showAmount} onChange={e => setShowAmount(e.target.checked)} /> Amount
                  </label>
                </div>
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3" style={{ paddingTop: "28px" }}>
                <button type="submit" className="btn btn-primary"><i className="fas fa-check me-1"></i>Submit</button>
              </div>
            </Col>
          </Row>

          {/* Company row - only if multiple */}
          {showCompany && <Row>
            <Col md={3}><div className="mb-3"><AvField type="select" name="company" label="Company" onChange={companyChange}><option value="">All</option>{companies.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</AvField></div></Col>
          </Row>}
        </AvForm>

        {loading && <div className="text-center py-4"><img className="loaderImg" src={loader} alt="Loading" /></div>}

        {submitted && !loading && (
          <>
            <div className="notToPrint" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", marginTop: "16px", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Showing <strong>{filtered.length}</strong> days
                {totals && <span className="ms-3">| Total Qty: <strong>{Number(totals.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong> Ltrs</span>}
                {totals && showAmount && <span className="ms-3">| Total Amount: <strong>₹{Number(totals.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong></span>}
              </div>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <input type="text" className="form-control form-control-sm" placeholder="Search..." style={{ width: "180px" }} value={search} onChange={e => setSearch(e.target.value)} />
                <button className="btn btn-sm btn-outline-success" onClick={exportCSV} title="Export CSV"><i className="fas fa-file-csv"></i></button>
                <button className="btn btn-sm btn-outline-danger" onClick={downloadPdf} title="Export PDF"><i className="fas fa-file-pdf"></i></button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => window.print()} title="Print"><i className="fas fa-print"></i></button>
              </div>
            </div>
            <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table className="table table-sm table-hover table-bordered mb-0" id="procHistoryTbl" style={{ fontSize: "11px" }}>
                <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                  <tr>
                    <th style={{ fontSize: "10px", color: "#64748b" }}>S.No</th>
                    <th style={{ fontSize: "10px", color: "#64748b" }}>Date</th>
                    <th style={{ fontSize: "10px", color: "#64748b" }}>Entries</th>
                    <th style={{ fontSize: "10px", color: "#64748b" }}>Suppliers</th>
                    {showQty && <th style={{ fontSize: "10px", color: "#64748b" }}>Quantity (Ltrs)</th>}
                    {showFat && <th style={{ fontSize: "10px", color: "#64748b" }}>Avg FAT</th>}
                    {showSnf && <th style={{ fontSize: "10px", color: "#64748b" }}>Avg SNF</th>}
                    {showRate && <th style={{ fontSize: "10px", color: "#64748b" }}>Avg Rate</th>}
                    {showAmount && <th style={{ fontSize: "10px", color: "#64748b" }}>Total Amount</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{r.date}</td>
                      <td>{r.entries}</td>
                      <td><span className="badge bg-info" style={{ fontSize: "10px" }}>{r.suppliers}</span></td>
                      {showQty && <td style={{ fontWeight: 700, color: "#0284c7" }}>{Number(r.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>}
                      {showFat && <td><span style={{ background: "#f0fdf4", color: "#166534", padding: "2px 8px", borderRadius: "8px" }}>{r.avgFat}</span></td>}
                      {showSnf && <td><span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "8px" }}>{r.avgSnf}</span></td>}
                      {showRate && <td>{Number(r.avgRate).toFixed(2)}</td>}
                      {showAmount && <td style={{ fontWeight: 600, color: "#16a34a" }}>₹{Number(r.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>}
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-4 text-muted">No records found</td></tr>}
                </tbody>
                {totals && filtered.length > 0 && (
                  <tfoot style={{ background: "#f1f5f9", fontWeight: 700 }}>
                    <tr>
                      <td colSpan={3}>TOTAL</td>
                      <td></td>
                      {showQty && <td style={{ color: "#0284c7" }}>{Number(totals.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>}
                      {showFat && <td>{totals.avgFat}</td>}
                      {showSnf && <td>{totals.avgSnf}</td>}
                      {showRate && <td>{Number(totals.avgRate).toFixed(2)}</td>}
                      {showAmount && <td style={{ color: "#16a34a" }}>₹{Number(totals.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ProcurementHistory
